/* Obchody directory realtime sync — catalog-parity path.
 *
 * Listens for `customer_directory_updated` on the shared `websocket-message`
 * bus (fed by usePublicDirectorySSE) and coalesces invalidation of public
 * customer-sales-points queries. On listable===false, optimistically remove
 * that salesPointId from infinite-query pages (inventory active=false parity).
 *
 * Connect catch-up uses Contract B: 30s cooldown across reconnect flaps;
 * storm latch arm sets an explicit catch-up bypass (not cooldown zero).
 *
 * G10 — tab-return refresh is owned by app-shell CustomerDirectoryGlobalSync only.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  createCoalescedRefetchScheduler,
  type CoalescedRefetchScheduler,
} from 'pi-kiosk-shared';
import {
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';
import {
  invalidateAllAccountMembershipsCache,
  invalidateAllSalesPointContactsCache,
} from '../../../shared/api/customerDirectoryCacheInvalidate.js';
import {
  invalidateAllShopKioskMetaCache,
  invalidateShopKioskMetaCache,
  invalidateShopKioskMetaForSalesPoint,
} from '../../../shared/api/shopKioskMetaCacheInvalidate.js';
import {
  isKioskUpdated,
  readSalesPointIdFromRealtimeMessage,
  readTenantCodeFromBrandingMessage,
} from '../../../shared/api/shopKioskMetaRealtimeEvents.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import type { PublicCustomerSalesPointsPage } from '../publicSalesPointsApi.js';
import {
  patchDirectoryDisplayInCaches,
  patchDirectoryGeoInCaches,
  readDirectoryDisplayFieldsFromPayload,
  readDirectoryGeoFromPayload,
  readDirectorySalesPointId,
} from '../../../shared/api/directoryGeoCachePatch.js';
import { usePublicDirectorySseConnected } from './PublicDirectorySseProvider.js';

/**
 * G6 — geo-bearing SSE events bypass the 400ms coalesce debounce when the
 * optimistic pin patch cannot apply (sales point not yet in loaded pages).
 */
export const DIRECTORY_GEO_URGENT_REFETCH_MAX_MS = 2_000;

/**
 * Match catalog `SSE_PRODUCT_REFRESH_COOLDOWN_MS` / kiosk handleSSEConnect.
 * Contract B: cooldown survives ordinary disconnect; storm latch arm sets
 * stormCatchUpPending bypass.
 */
export const SSE_DIRECTORY_REFRESH_COOLDOWN_MS = 30_000;

/** Higher staleTime while public directory SSE is live (catalog parity). */
export const DIRECTORY_STALE_TIME_SSE_MS = 300_000;

/** Poll when public directory SSE is down — `/shops` + allowlist screens. */
export const DIRECTORY_REFETCH_INTERVAL_MS = 30_000;

/**
 * Tab/focus return cooldown — owned by app-shell `CustomerDirectoryGlobalSync` (G10).
 * Screen hooks rely on global sync + React Query `refetchOnWindowFocus`.
 */
export const VISIBILITY_DIRECTORY_REFRESH_COOLDOWN_MS = 5_000;

/**
 * G7 — catalog-parity poll: 30s while SSE disconnected; disable when connected.
 * Screens must pass the boolean returned by `useShopsDirectoryRealtimeSync()`.
 */
export function directoryListRefetchInterval(sseConnected: boolean): number | false {
  return sseConnected ? false : DIRECTORY_REFETCH_INTERVAL_MS;
}

/** Shared React Query options for `/shops` directory + map (Obchody parity). */
export function shopsDirectoryQueryOptions(sseConnected: boolean): {
  staleTime: number;
  refetchInterval: number | false;
  refetchOnWindowFocus: true;
} {
  return {
    staleTime: sseConnected ? DIRECTORY_STALE_TIME_SSE_MS : 60_000,
    refetchInterval: directoryListRefetchInterval(sseConnected),
    refetchOnWindowFocus: true,
  };
}

interface DirectoryUpdateMessage {
  type?: string;
  data?: Record<string, unknown>;
}

type DirectoryInfiniteCache = InfiniteData<PublicCustomerSalesPointsPage, string | null>;

function isCustomerDirectoryUpdated(message: DirectoryUpdateMessage): boolean {
  if (message.type === 'customer_directory_updated') {
    return true;
  }
  const nestedType = message.data?.type;
  return nestedType === 'customer_directory_updated';
}

/**
 * Vzhled / logo brand-kit — refresh Obchody chips with tenant thumbs.
 * Prefix-invalidates public sales-points (incl. Favorites allowlist) so guest
 * pending LS branding can re-merge from live DTOs (G8).
 */
function isTenantBrandingUpdated(message: DirectoryUpdateMessage): boolean {
  if (message.type === 'tenant_branding_updated') {
    return true;
  }
  const nestedType = message.data?.type;
  return nestedType === 'tenant_branding_updated';
}

/** Payload carrying listable / salesPointId (top-level or nested envelope). */
function directoryUpdatePayload(
  message: DirectoryUpdateMessage,
): Record<string, unknown> | undefined {
  if (message.type === 'customer_directory_updated') {
    return message.data;
  }
  if (message.data?.type === 'customer_directory_updated') {
    return message.data;
  }
  return undefined;
}

function isDirectoryInfiniteData(value: unknown): value is DirectoryInfiniteCache {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const pages = (value as { pages?: unknown }).pages;
  return Array.isArray(pages);
}

function filterSalesPointFromInfinitePages(
  oldData: DirectoryInfiniteCache,
  salesPointId: number,
): DirectoryInfiniteCache {
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      items: page.items.filter((item) => item.salesPointId !== salesPointId),
    })),
  };
}

export function useShopsDirectoryRealtimeSync(): boolean {
  const queryClient = useQueryClient();
  const { apiBaseUrl } = useRuntimeConfig();
  const queryClientRef = useRef(queryClient);
  const apiBaseUrlRef = useRef(apiBaseUrl);
  const coalescedRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);
  const lastSseDirectoryRefreshRef = useRef(0);
  /** Next SSE open bypasses connect-refresh cooldown after storm latch arm. */
  const stormCatchUpPendingRef = useRef(false);

  useEffect(() => {
    queryClientRef.current = queryClient;
    apiBaseUrlRef.current = apiBaseUrl;
  }, [queryClient, apiBaseUrl]);

  useEffect(() => {
    if (coalescedRefetchRef.current === null) {
      coalescedRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => ['public', 'customer-sales-points', apiBaseUrlRef.current],
      );
    }
    const coalescedRefetch = coalescedRefetchRef.current;
    return (): void => {
      coalescedRefetch.cancel();
    };
  }, []);

  // Contract B — arm catch-up when storm latches (incl. already-latched mount).
  // Do not zero lastSseDirectoryRefreshRef on ordinary disconnect.
  useEffect(() => {
    if (isHttpStormLatched()) {
      stormCatchUpPendingRef.current = true;
    }
    return subscribeHttpStormLatch(() => {
      if (isHttpStormLatched()) {
        stormCatchUpPendingRef.current = true;
      }
    });
  }, []);

  const scheduleDirectoryRefetch = useCallback((): void => {
    coalescedRefetchRef.current?.schedule();
  }, []);

  /** G6 — zero-debounce flush when geo patch misses loaded cache pages. */
  const scheduleUrgentDirectoryRefetch = useCallback((): void => {
    void coalescedRefetchRef.current?.flush();
  }, []);

  const refreshShopKioskMetaFromDirectoryEvent = useCallback(
    (message: DirectoryUpdateMessage): void => {
      if (isKioskUpdated(message)) {
        const salesPointId = readSalesPointIdFromRealtimeMessage(message);
        if (salesPointId !== undefined) {
          invalidateShopKioskMetaForSalesPoint(queryClientRef.current, salesPointId);
        } else {
          invalidateAllShopKioskMetaCache(queryClientRef.current);
        }
        return;
      }
      if (isTenantBrandingUpdated(message)) {
        const tenantCode = readTenantCodeFromBrandingMessage(message);
        if (tenantCode !== undefined) {
          invalidateShopKioskMetaCache(queryClientRef.current, tenantCode);
        } else {
          invalidateAllShopKioskMetaCache(queryClientRef.current);
        }
        return;
      }
      if (isCustomerDirectoryUpdated(message)) {
        const salesPointId = readSalesPointIdFromRealtimeMessage(message);
        if (salesPointId !== undefined) {
          invalidateShopKioskMetaForSalesPoint(queryClientRef.current, salesPointId);
        } else {
          invalidateAllShopKioskMetaCache(queryClientRef.current);
        }
      }
    },
    [],
  );

  const isConnected = usePublicDirectorySseConnected();

  // Connect catch-up: Contract B cooldown + storm latch bypass.
  useEffect(() => {
    if (!isConnected) {
      // Keep lastSseDirectoryRefreshRef across disconnect so non-storm
      // reconnect flaps honor the 30s cooldown.
      return;
    }
    const now = Date.now();
    const stormBypass = stormCatchUpPendingRef.current;
    if (stormBypass) {
      stormCatchUpPendingRef.current = false;
    } else if (now - lastSseDirectoryRefreshRef.current < SSE_DIRECTORY_REFRESH_COOLDOWN_MS) {
      return;
    }
    lastSseDirectoryRefreshRef.current = now;
    scheduleDirectoryRefetch();
  }, [isConnected, scheduleDirectoryRefetch]);

  useEffect(() => {
    const handleWebSocketMessage = (event: Event): void => {
      try {
        const customEvent = event as CustomEvent<{ data: string }>;
        const raw = customEvent.detail?.data;
        if (typeof raw !== 'string' || raw.length === 0) {
          return;
        }
        const message = JSON.parse(raw) as DirectoryUpdateMessage;
        if (isKioskUpdated(message)) {
          refreshShopKioskMetaFromDirectoryEvent(message);
          return;
        }
        if (isTenantBrandingUpdated(message)) {
          // G7 — Oblíbené/contacts thumbs (tenantLogoUrl + rim) live on
          // salesPointContacts, not public sales-points. Prefix-invalidate all
          // customerKeys; this hook has no customerKey in scope.
          invalidateAllSalesPointContactsCache(queryClientRef.current);
          // G9 — memberships DTO carries logoUrl + rim; no tenant/customer in scope.
          invalidateAllAccountMembershipsCache(queryClientRef.current);
          refreshShopKioskMetaFromDirectoryEvent(message);
          scheduleDirectoryRefetch();
          return;
        }
        if (!isCustomerDirectoryUpdated(message)) {
          return;
        }

        refreshShopKioskMetaFromDirectoryEvent(message);

        const payload = directoryUpdatePayload(message);
        const listable = payload?.listable;
        const salesPointId = readDirectorySalesPointId(payload);

        // G11 — listable===false: optimistic remove from infinite pages, then coalesce.
        if (listable === false && salesPointId !== null) {
          const prefixKey = ['public', 'customer-sales-points', apiBaseUrlRef.current] as const;
          queryClientRef.current.setQueriesData<unknown>(
            { queryKey: prefixKey },
            (oldData: unknown) => {
              if (!isDirectoryInfiniteData(oldData)) {
                return oldData;
              }
              return filterSalesPointFromInfinitePages(oldData, salesPointId);
            },
          );
        } else {
          const geo = readDirectoryGeoFromPayload(payload);
          const displayFields = readDirectoryDisplayFieldsFromPayload(payload);
          let geoPatchApplied = false;

          if (geo !== null && salesPointId !== null) {
            geoPatchApplied = patchDirectoryGeoInCaches(
              queryClientRef.current,
              apiBaseUrlRef.current,
              salesPointId,
              geo.lat,
              geo.lng,
            );
          }

          if (displayFields !== null && salesPointId !== null) {
            patchDirectoryDisplayInCaches(
              queryClientRef.current,
              apiBaseUrlRef.current,
              salesPointId,
              displayFields,
            );
          }

          if (geo !== null && salesPointId !== null && !geoPatchApplied) {
            scheduleUrgentDirectoryRefetch();
            return;
          }
        }

        scheduleDirectoryRefetch();
      } catch {
        return;
      }
    };

    const handleForceRefresh = (): void => {
      scheduleDirectoryRefetch();
    };

    window.addEventListener('websocket-message', handleWebSocketMessage);
    window.addEventListener('force-refresh', handleForceRefresh);

    return (): void => {
      window.removeEventListener('websocket-message', handleWebSocketMessage);
      window.removeEventListener('force-refresh', handleForceRefresh);
    };
  }, [scheduleDirectoryRefetch, scheduleUrgentDirectoryRefetch, refreshShopKioskMetaFromDirectoryEvent]);

  return isConnected;
}
